import { useCallback, useEffect, useState } from 'react';

import type { PickInput, PicksResponse } from '@lifetimeline/shared';

import {
  PickGrid,
  PickGridAddCell,
  PickGridCell,
  PickGridGhostCell,
  SortablePickGrid,
} from '../components/PickGrid.js';
import { Button } from '../components/Button.js';
import { ConfirmDialog } from '../components/ConfirmDialog.js';
import { Notice } from '../components/Notice.js';
import { Spinner } from '../components/Spinner.js';
import { useToast } from '../components/Toast.js';
import { useAuth } from '../features/auth/AuthContext.js';
import { usePickFlow } from '../features/pick/PickFlow.js';
import { useI18n } from '../i18n/index.js';
import { api, errorMessageKey, isAuthError } from '../lib/apiClient.js';
import { outputType } from '../lib/imageFile.js';
import { uploadImage } from '../lib/imageUpload.js';
import { categoryName, loadCatalog, type LoadedCatalog } from '../lib/catalog.js';
import styles from './pageStyles.module.css';

/**
 * 내 취향 — 카테고리마다 하나씩 고른 것들.
 *
 * 홈의 한 탭으로 들어간다 — 셸(`Layout`)과 로그인 확인은 홈이 한다.
 */
export function PicksPanel() {
  const { t, language } = useI18n();
  const { signOut } = useAuth();
  const toast = useToast();

  const [picks, setPicks] = useState<PickInput[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [catalog, setCatalog] = useState<LoadedCatalog | null>(null);
  // 삭제는 되돌릴 수 없다
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadCatalog(language)
      .then((loaded) => {
        if (!cancelled) {
          setCatalog(loaded);
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [language]);

  const load = useCallback(async () => {
    setError(null);
    try {
      const response = await api.get<PicksResponse>('/api/me/picks');
      setPicks(
        response.picks.map((pick) => ({
          categoryId: pick.categoryId,
          itemName: pick.itemName,
          itemImageUrl: pick.itemImageUrl,
          ...(pick.itemDescription === undefined ? {} : { itemDescription: pick.itemDescription }),
        })),
      );
    } catch (cause) {
      if (isAuthError(cause)) {
        signOut();
        return;
      }
      setError(t(errorMessageKey(cause)));
      setPicks([]);
    }
  }, [t, signOut]);

  useEffect(() => {
    void load();
  }, [load]);

  const flow = usePickFlow({
    existingPicks: picks ?? [],
    resolveImageUrl: (categoryId) =>
      picks?.find((pick) => pick.categoryId === categoryId)?.itemImageUrl ?? null,
    onSave: async (draft) => {
      // 사진을 먼저 올리고 그 주소로 저장한다. 순서가 반대면 주소 없는 Pick이
      // 저장됐다가 업로드가 실패해 사진 없는 항목이 남는다.
      const itemImageUrl =
        draft.imageBlob === null
          ? draft.keptImageUrl
          : await uploadImage(draft.imageBlob, outputType());

      /*
       * 사진이 없으면 **필드를 빼고** 보낸다. 빈 문자열을 보내면 안 된다 —
       * `itemImageUrl`은 https 주소여야 해서 `''`는 스키마에서 걸리고,
       * 사진 없는 픽은 아예 저장이 안 됐다. 사진은 선택인데 사진 없는 쪽이
       * 막혀 있었다.
       *
       * 빼면 지워진다 — 저장이 통째로 덮어쓰기라 없는 필드는 사라진다.
       * 사진을 지우고 저장하는 경우도 이 길로 처리된다.
       */
      await api.put(`/api/me/picks/${draft.categoryId}`, {
        itemName: draft.itemName,
        ...(itemImageUrl === null || itemImageUrl === '' ? {} : { itemImageUrl }),
        ...(draft.itemDescription === undefined ? {} : { itemDescription: draft.itemDescription }),
      });
      await load();
      toast.show(t('picks.saved'));
    },
    onRequestDelete: setPendingDelete,
  });

  /**
   * 순서를 바꾼다.
   *
   * 화면을 **먼저** 바꾸고 서버에 보낸다. 끌어다 놓은 것이 손을 떼자마자
   * 제자리로 돌아갔다가 잠시 뒤 다시 움직이면 끊겨 보인다.
   *
   * 실패하면 서버가 준 목록으로 되돌린다 — 화면만 바뀐 채로 두면 새로고침
   * 한 번에 순서가 사라진 것처럼 보인다.
   */
  const reorder = (next: string[]) => {
    const previous = picks;
    if (previous === null) {
      return;
    }
    const byId = new Map(previous.map((pick) => [pick.categoryId, pick]));
    setPicks(next.map((id) => byId.get(id)).filter((pick) => pick !== undefined));

    void (async () => {
      try {
        const response = await api.put<PicksResponse>('/api/me/picks/order', {
          categoryIds: next,
        });
        setPicks(
          response.picks.map((pick) => ({
            categoryId: pick.categoryId,
            itemName: pick.itemName,
            ...(pick.itemDescription === undefined
              ? {}
              : { itemDescription: pick.itemDescription }),
            ...(pick.itemImageUrl === undefined ? {} : { itemImageUrl: pick.itemImageUrl }),
          })),
        );
      } catch (cause) {
        setPicks(previous);
        setError(t(errorMessageKey(cause)));
      }
    })();
  };

  const remove = async (categoryId: string) => {
    try {
      await api.delete(`/api/me/picks/${categoryId}`);
      await load();
    } catch (cause) {
      toast.show(t(errorMessageKey(cause)), 'error');
    }
  };

  return (
    <>
      <div className={styles.head}>
        <div className={styles.headRow}>
          {/*
            숫자를 적지 않는다. 격자에 칸이 몇 개인지가 이미 보인다 —
            타임라인에서 주제·기록 수를 뺀 것과 같은 이유다.
          */}
        </div>
      </div>

      {error !== null ? (
        <div className={styles.noticeSlot}>
          <Notice tone="error">{error}</Notice>
        </div>
      ) : null}

      {picks === null ? (
        <div className={styles.centered}>
          <Spinner label={t('common.loading')} />
        </div>
      ) : picks.length === 0 ? (
        /*
          비어 있어도 **같은 격자, 같은 추가 칸**이다.

          예전에는 빈 화면만 점선 상자 + 큰 버튼이라, 하나를 고르는 순간
          추가하는 자리가 화면 가운데에서 격자 끝으로 옮겨 갔다. 처음 한 번
          배운 자리가 곧바로 무효가 되는 셈이다. 지금은 첫 칸이 그대로
          남아 뒤로 밀릴 뿐이다.
        */
        <>
          <PickGrid>
            <PickGridAddCell label={t('picks.addOne')} onClick={flow.openSearch} />
            <PickGridGhostCell />
            <PickGridGhostCell />
          </PickGrid>
        </>
      ) : (
        <>
          <SortablePickGrid order={picks.map((pick) => pick.categoryId)} onReorder={reorder}>
            <PickGridAddCell label={t('picks.addOne')} onClick={flow.openSearch} />
            {picks.map((pick) => {
              const name =
                catalog === null ? pick.categoryId : categoryName(catalog, pick.categoryId);
              return (
                <PickGridCell
                  key={pick.categoryId}
                  item={{
                    categoryId: pick.categoryId,
                    categoryName: name,
                    itemName: pick.itemName,
                    ...(pick.itemImageUrl === undefined ? {} : { imageUrl: pick.itemImageUrl }),
                  }}
                  label={`${name} — ${pick.itemName}`}
                  sortable
                  onClick={() => flow.openEdit(pick.categoryId)}
                />
              );
            })}
          </SortablePickGrid>
        </>
      )}

      <ConfirmDialog
        open={pendingDelete !== null}
        destructive
        confirmLabel={t('common.delete')}
        title={t('picks.deleteConfirm', {
          category:
            pendingDelete !== null && catalog !== null ? categoryName(catalog, pendingDelete) : '',
        })}
        body={t('picks.deleteConfirmBody')}
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => {
          const target = pendingDelete;
          setPendingDelete(null);
          if (target !== null) {
            void remove(target);
          }
        }}
      />

      {flow.element}
    </>
  );
}
